import { defineStore } from "pinia";
import { getTranslationChunked } from "@/composables/text-generator/useOpenAiApi";
import { useGeneralStore } from "./generalStore";
import { useMjmlStore } from "./mjmlStore";
import { useNotificationsStore } from "./notifications";
import { i18nGlobal } from "@/init/i18n-setup";
import { iso639 } from "@topol/utils";
import { TranslationUnit } from "@/schemas/template/languageSchema";

interface State {
  // This is comming directly from the backend
  openAiTokenPresent: boolean;
  // This is for the case where the user has disabled openAi
  // in the options and want to disable it for some users
  openAiDisabledInOptions: boolean;
}

export const useOpenAiStore = defineStore("openAiStore", {
  state: (): State => ({
    openAiTokenPresent: false,
    openAiDisabledInOptions: false,
  }),

  getters: {
    openAiEnabled(): boolean {
      return this.openAiTokenPresent && !this.openAiDisabledInOptions;
    },
  },

  actions: {
    async translateText(
      lang: string,
      sourceLang?: string,
      targetLang?: string
    ) {
      const generalStore = useGeneralStore();
      const mjmlStore = useMjmlStore();
      generalStore.translationInProgress = true;
      const units: TranslationUnit[] = mjmlStore.getTranslatableUnits({
        ignoreSynced: true,
        notifySyncedIgnore: true,
        sourceLang,
      });
      const payload = units.map((u) => ({ uid: u.id, content: u.content }));
      const result = await getTranslationChunked(payload, lang);
      if (result) {
        mjmlStore.applyTranslationUnits(
          units,
          result.translations,
          targetLang,
          false
        );
        const { t } = i18nGlobal;
        useNotificationsStore().createNotification({
          type: "info",
          title: t("translation.completed"),
          text: t("translation.detected_language", {
            language: result.detectedLanguage,
          }),
        });
      }
      generalStore.translationInProgress = false;
    },

    // Translate a single mutation, identified by its language code, from a
    // source mutation (defaults to the primary). Resolves the display name the
    // translation API expects. Used by the TopolPlugin.translateLanguage API.
    async translateSingleLanguage(targetCode: string, sourceCode?: string) {
      const mjmlStore = useMjmlStore();
      const generalStore = useGeneralStore();
      const { t } = i18nGlobal;

      const langs = mjmlStore.mjml.langs;
      if (!langs?.find((l) => l.key === targetCode)) {
        useNotificationsStore().createNotification({
          type: "error",
          text: t("layout.language_not_found"),
        });
        return;
      }

      const langsToUse = generalStore.customLanguagePreset ?? iso639;
      const langObject = langsToUse.find((l) => l.code === targetCode);
      const langName = langObject
        ? `${langObject.name} (${langObject.code})`
        : targetCode;
      const source = sourceCode ?? langs.find((l) => l.primary)?.key;

      await this.translateText(langName, source, targetCode);
    },

    async translateAllFromPrimary(
      langsToUse?: { code: string; name: string }[]
    ) {
      const generalStore = useGeneralStore();
      const mjmlStore = useMjmlStore();
      const { t } = i18nGlobal;
      const notifications = useNotificationsStore();

      const resolvedLangs =
        langsToUse ?? generalStore.customLanguagePreset ?? iso639;

      const langs = mjmlStore.mjml.langs;
      if (!langs?.length) return;
      const primary = langs.find((l) => l.primary);
      if (!primary) return;

      // Every non-primary mutation is (re)translated from the primary; existing
      // translations are overwritten.
      const targets = langs.filter((l) => !l.primary);
      if (!targets.length) {
        notifications.createNotification({
          type: "info",
          text: t("translation.nothing_to_translate"),
        });
        return;
      }

      // Source content is identical for every target, so collect it once from
      // the primary mutation. ignoreSynced mirrors the single-language flow.
      const sourceUnits: TranslationUnit[] = mjmlStore.getTranslatableUnits({
        ignoreSynced: true,
        notifySyncedIgnore: true,
        sourceLang: primary.key,
      });
      const payload = sourceUnits.map((u) => ({
        uid: u.id,
        content: u.content,
      }));

      // Single snapshot for the whole batch so one undo reverts everything.
      mjmlStore.takeSnapshot();
      generalStore.translationInProgress = true;

      let succeeded = 0;
      let failed = 0;
      for (const target of targets) {
        const langObject = resolvedLangs.find((l) => l.code === target.key);
        const langName = langObject
          ? `${langObject.name} (${langObject.code})`
          : target.key;
        const result = await getTranslationChunked(payload, langName);
        if (result) {
          mjmlStore.applyTranslationUnits(
            sourceUnits,
            result.translations,
            target.key,
            true
          );
          succeeded++;
        } else {
          failed++;
        }
      }

      generalStore.translationInProgress = false;

      notifications.createNotification({
        type: failed > 0 ? "error" : "info",
        title: t("translation.completed"),
        text: t("translation.bulk_completed", {
          succeeded,
          total: targets.length,
        }),
      });
    },
  },
});
